import { useCallback } from 'react';

import { getOrderLeverage } from 'actions/orderbook';
import { SIDE } from 'constants/enums';
import { calculateExtraOrderMargin } from 'helpers/formulas';
import { filter, map, pick, pipe, reduce, sort } from 'helpers/ramda';
import useCustomMediaQuery from 'hooks/useMediaQuery';
import {
  basketActiveUnderlyingAssetSelector,
  basketOrderOrdersSelector,
} from 'selectors/basketOrdersSelectors';
import {
  allOpenOrdersSelector,
  allOpenPositionsSelector,
} from 'selectors/holdingsSelectors';
import {
  markPriceSelectorById,
  spotPriceSelectorBySymbol,
} from 'selectors/priceSelectors';
import { productsSelector, selectedProductSelector } from 'selectors/tradeSelectors';
import { useAppDispatch, useAppSelector } from 'storeHooks';
import { IOrdersObject } from 'types/basketOrders';

export function useOrderMargin() {
  const dispatch = useAppDispatch();
  const products = useAppSelector(state => productsSelector(state));
  const allOpenPositions = useAppSelector(state => allOpenPositionsSelector(state));
  const allOpenOrders = useAppSelector(state => allOpenOrdersSelector(state));

  const product = useAppSelector(selectedProductSelector);

  const ordersInBasket: IOrdersObject = useAppSelector(state =>
    basketOrderOrdersSelector(state)
  );
  const mobileActiveAssetSymbol = useAppSelector(basketActiveUnderlyingAssetSelector);

  const { isDesktop } = useCustomMediaQuery();
  const { underlying_asset: underlyingAsset } = product || {};
  const selectedAssetSymbol = !isDesktop
    ? mobileActiveAssetSymbol
    : underlyingAsset?.symbol;

  const liveOrdersInBasket = Object.values(ordersInBasket).filter(order => {
    return (
      products[order.product_id] &&
      order.asset === selectedAssetSymbol &&
      // if users only entered qty and didn't clicked buy/sell and click done from products screen
      // we should take only the ones which user selected buy/sell orders
      order.side !== ''
    );
  });

  // markprice
  const price = (productId: number) => {
    const markPrice = markPriceSelectorById(String(productId));
    return markPrice;
  };

  // spotprice
  const getSpotPrice = spotIndex => {
    const spotPrice = spotPriceSelectorBySymbol(spotIndex);
    return spotPrice;
  };

  // position
  const getPosition = (productId: number) => {
    return allOpenPositions.filter(position => position.product_id === productId);
  };

  // fetch leverage
  const fetchAllLeverage = useCallback(() => {
    return Promise.all(
      liveOrdersInBasket.map(order => dispatch(getOrderLeverage(order.product_id)))
    );
  }, [liveOrdersInBasket, dispatch]);

  // orders
  const getOrders = (productId: number, side: SIDE) => {
    const productOpenOrdersBySide = pipe(
      filter(openOrder => openOrder.product_id === productId && side === SIDE.BUY),
      map(pick(['unfilled_size', 'limit_price'])),
      sort((a, b) => {
        const x = Number(a.limit_price);
        const y = Number(b.limit_price);
        return y - x;
      })
    )(allOpenOrders);
    return productOpenOrdersBySide;
  };

  const getUpdatedMargin = async () => {
    const result = await fetchAllLeverage();
    const newOrderMargins = {};
    result?.forEach(o => {
      if (o?.result) {
        newOrderMargins[o?.result?.product_id] = o?.result;
      }
    });
    const calculateMargin = (acc, order) => {
      if (!newOrderMargins[order.product_id]) return acc;

      const productMargin = calculateExtraOrderMargin({
        mark_price: price(order.product_id),
        product: products[order.product_id],
        position: getPosition(order.product_id) || 0,
        leverage: Number(newOrderMargins[order.product_id].leverage),
        order_margin: Number(newOrderMargins[order.product_id].order_margin),
        user_buy_orders: getOrders(order.product_id, SIDE.BUY),
        user_sell_orders: getOrders(order.product_id, SIDE.SELL),
        side: order.side,
        size: order.size,
        limit_price: Number(order.limit_price) || price(order.product_id),
        spot_price: getSpotPrice(products[order.product_id].spot_index?.symbol),
      });
      return acc + productMargin;
    };
    const totalMargin = reduce(calculateMargin, 0, liveOrdersInBasket);
    return Number(totalMargin).toFixed(2);
  };

  return { selectedAssetSymbol, liveOrdersInBasket, fetchAllLeverage, getUpdatedMargin };
}
